import React, { useRef, useCallback } from 'react';
import { Button, type ButtonProps } from '@mui/material';
import { gsap } from 'gsap';

export interface SpinBurstButtonProps extends Omit<ButtonProps, 'variant'> {
  /** MUI button variant */
  muiVariant?: ButtonProps['variant'];
  /** Icon element to animate */
  icon?: React.ReactNode;
  /** Icon position */
  iconPosition?: 'start' | 'end';
  /** Children/button content */
  children: React.ReactNode;
}

/**
 * SpinBurstButton - 360° spin burst with wiggle on hover
 * 
 * Button scales on hover with icon wiggle, icon spins 360° on click.
 * Perfect for share/send actions.
 * 
 * @example
 * ```tsx
 * <SpinBurstButton icon={<ShareIcon />} iconPosition="start">
 *   Share
 * </SpinBurstButton>
 * ```
 */
export function SpinBurstButton({
  muiVariant = 'contained',
  icon,
  iconPosition = 'start',
  children,
  onClick,
  ...props
}: SpinBurstButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const iconRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = useCallback(() => {
    if (buttonRef.current) {
      gsap.to(buttonRef.current, {
        scale: 1.03,
        duration: 0.3,
        ease: 'power2.out',
      });
    }
    
    // Subtle icon tilt
    if (iconRef.current) {
      gsap.to(iconRef.current, {
        rotation: 8,
        duration: 0.2,
        ease: 'power2.out',
      });
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (buttonRef.current) {
      gsap.to(buttonRef.current, {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out',
      });
    }
    
    // Reset icon rotation
    if (iconRef.current) {
      gsap.to(iconRef.current, {
        rotation: 0,
        duration: 0.2,
        ease: 'power2.out',
      });
    }
  }, []);

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (iconRef.current) {
      // Gentle pulse instead of full spin
      gsap.timeline()
        .to(iconRef.current, {
          scale: 1.2,
          rotation: 45,
          duration: 0.2,
          ease: 'power2.out',
        })
        .to(iconRef.current, {
          scale: 1,
          rotation: 0,
          duration: 0.3,
          ease: 'back.out(1.5)',
        });
    }
    onClick?.(e);
  }, [onClick]);

  const iconElement = icon ? (
    <span 
      ref={iconRef}
      style={{ 
        display: 'inline-flex', 
        alignItems: 'center',
        willChange: 'transform',
      }}
    >
      {icon}
    </span>
  ) : null;

  return (
    <Button
      ref={buttonRef}
      variant={muiVariant}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      {...props}
      sx={{
        willChange: 'transform',
        ...props.sx,
      }}
    >
      {iconPosition === 'start' && iconElement}
      {children}
      {iconPosition === 'end' && iconElement}
    </Button>
  );
}

export default SpinBurstButton;
